import React, { useEffect, useMemo } from 'react';
import { Helmet, HelmetProps } from 'react-helmet-async';
import { RouterProvider } from 'react-router';
import { router } from './routes/router';
import { ReduxState, useAppDispatch } from './store';
import Auth from './auth/Auth';
import ToastContainer from './components/toasts/ToastContainer';
import { ApplicationStatusOfflinePage, ApplicationStatusUpgradingPage, Global } from '@ydistri/ds';
import { useSelector } from 'react-redux';
import { signalrClient } from './signalr/client';
import { setStatus, setVersion, SignalSendCurrentFrontendStatus } from './store/appSlice';
import { AvailableModals, openModal } from './screens/Modals/modalSlice';
import { createDebugLog } from '@ydistri/utils';

const debugLog = createDebugLog('App');

export const auth = new Auth();

/**
 * Check if a new version is available by comparing semantic version strings.
 * Version strings may contain environment info after a hyphen (e.g., "1.2.3-dev"), that is
 * why we extract numbers using regex and compare them.
 * @param appVersionFromEnv Version stored in the app environment (e.g., VITE_REACT_APP_VERSION)
 * @param appVersionFromSignalr Version received from SignalR as maintained by the backend
 */
export function isNewVersionAvailable(
  appVersionFromEnv: string,
  appVersionFromSignalr: string,
): boolean {
  // major, minor and patch versions are extracted from given strings
  const versionRegex = /^(\d+)\.(\d+)\.(\d+)/;

  const parse = (version: string): [number, number, number] | null => {
    const match = versionRegex.exec(version);
    return match ? [parseInt(match[1], 10), parseInt(match[2], 10), parseInt(match[3], 10)] : null;
  };

  const envVersion = parse(appVersionFromEnv);
  const signalrVersion = parse(appVersionFromSignalr);

  if (!envVersion || !signalrVersion) {
    return false; // invalid version string
  }

  for (let i = 0; i < 3; i++) {
    if (signalrVersion[i] > envVersion[i]) return true;
    if (signalrVersion[i] < envVersion[i]) return false;
  }

  return false;
}

const App: React.FC = () => {
  const dispatch = useAppDispatch();
  const appStatus = useSelector((state: ReduxState) => state.appReducer.status);
  const appVersionFromSignalr = useSelector((state: ReduxState) => state.appReducer.version);

  useEffect(() => {
    signalrClient.on('SendCurrentFrontendStatus', (response: SignalSendCurrentFrontendStatus) => {
      debugLog('Current frontend status changed! ' + response);
      dispatch(setStatus(response));
    });

    signalrClient.on('SendCurrentFrontendVersion', (response: string) => {
      debugLog('Current frontend version changed! ' + response);
      dispatch(setVersion(response));
    });

    return () => {
      signalrClient.off('SendCurrentFrontendStatus');
      signalrClient.off('SendCurrentFrontendVersion');
    };
  }, [dispatch]);

  useEffect(() => {
    const appVersionFromEnv = import.meta.env.VITE_REACT_APP_VERSION;
    debugLog(`App versions - ENV ${appVersionFromEnv} | SignalR: ${appVersionFromSignalr}`);
    if (appVersionFromSignalr && appVersionFromEnv) {
      if (isNewVersionAvailable(appVersionFromEnv, appVersionFromSignalr)) {
        dispatch(openModal({ type: AvailableModals.NEW_APP_VERSION, data: undefined }));
      }
    }
  }, [appVersionFromSignalr, dispatch]);

  useEffect(() => {
    auth.bootstrapSessionFromMicrosoftAccount().catch(() => {
      // Ignore bootstrap errors on app startup; interactive sign-in flow handles auth.
    });
  }, []);

  const helmetLink: HelmetProps['link'] = useMemo(
    () => [
      {
        rel: 'stylesheet',
        href: 'https://fonts.googleapis.com/css?family=Rubik:200,400,500,600&subset=latin-ext',
      },
    ],
    [],
  );

  auth.syncAuthenticationHeadersFromStoredToken();

  switch (appStatus) {
    case 'Upgrading':
      return <ApplicationStatusUpgradingPage />;
    case 'Offline':
      return <ApplicationStatusOfflinePage />;
    case 'Online':
    case undefined:
      return (
        <Global>
          <Helmet titleTemplate="Ydistri - %s" defaultTitle="Ydistri" link={helmetLink} />
          <RouterProvider router={router} />
          <ToastContainer />
        </Global>
      );
  }
};

export default App;
